import React from 'react';
import { InputAudioElement } from '@app/common';
import TextWithReferences from '../../TextWithReferences/TextWithReferences';
import { AudioService } from 'src/services/CordovaService/AudioService';
import RequiredAsterisk from '../RequiredAsterisk';
import AudioRecordingRow from './AudioRecordingRow/AudioRecordingRow';
import { useAppSelector } from 'src/store/hooks';

interface Props {
    docId: string | undefined;
    moduleId: string | null;
    elementId: string;
    data: InputAudioElement;
    value?: string;
    setValue: (value: string | null) => void;
    setBlob: (elementId: string, blob: Blob | null) => void;
}

const InputAudio: React.FC<Props> = ({
    docId,
    moduleId,
    elementId,
    data,
    value,
    setValue,
    setBlob
}) => {
    const [isRecording, setIsRecording] = React.useState(false);
    const [audioUrl, setAudioUrl] = React.useState<string | null>(null);
    const [justRecorded, setJustRecorded] = React.useState(false);

    const mediaRecorderRef = React.useRef<MediaRecorder | null>(null);
    const fetchIdRef = React.useRef(0);
    const suppressFetchRef = React.useRef(false);

    const audioService = new AudioService();
    const userCode = useAppSelector(state => state.globalState.user?.code);

    const filename = React.useMemo(() => {
        if (moduleId) {
            return audioService.getAudioFileName(userCode, moduleId);
        }
        return null;
    }, [audioService, userCode, moduleId]);

    const setAudioURLToNull = () => {
        setAudioUrl(prev => {
            if (!prev || (!prev.startsWith('data:') && !prev.startsWith('file:'))) {
                return null;
            }
            return prev;
        });
    };

    const fetchAudioUrl = async () => {
        if (suppressFetchRef.current) {
            return;
        }
        const currentFetchId = ++fetchIdRef.current;

        if (value && filename) {
            try {
                const dbFilePath = value.split('|||')[1];
                const iosLocalFilename = dbFilePath.substring(dbFilePath.lastIndexOf('/') + 1);
                console.log('[InputAudio] fetchAudioUrl iosLocalFilename: ', iosLocalFilename);
                const _audioUrl = await audioService.getAttachedAudioRecordingAudioUrl(
                    docId,
                    filename,
                    iosLocalFilename
                );

                if (currentFetchId === fetchIdRef.current) {
                    if (_audioUrl) {
                        setAudioUrl(_audioUrl);
                    } else {
                        setAudioURLToNull();
                    }
                }
            } catch (err) {
                console.error('[fetchAudioUrl]', err);
                setAudioURLToNull();
            }
        } else {
            setAudioURLToNull();
        }
    };

    React.useEffect(() => {
        fetchAudioUrl();
    }, [docId, filename, value]);

    // after recording, lift suppression after cooldown
    React.useEffect(() => {
        if (!justRecorded) return;

        const t = window.setTimeout(() => {
            suppressFetchRef.current = false;
            setJustRecorded(false);
        }, 800);

        return () => window.clearTimeout(t);
    }, [justRecorded]);

    const handleToggleRecording = async () => {
        if (isRecording) {
            if (mediaRecorderRef.current?.state === 'recording') {
                mediaRecorderRef.current.stop();
            }
            setIsRecording(false);
            return;
        }

        try {
            const mediaRecorder = await audioService.attemptStartRecording((newBlob, url) => {
                // lock out fetch while recording just finished
                suppressFetchRef.current = true;
                fetchIdRef.current++;

                setJustRecorded(true);
                setAudioUrl(url);

                setBlob(elementId, newBlob);
                setValue('__audio:attached|||' + url);
            });

            mediaRecorderRef.current = mediaRecorder;
            setIsRecording(true);
        } catch (err) {
            console.error('[handleToggleRecording] start recording failed', err);
        }
    };

    const deleteRecording = () => {
        if (!docId || !filename) {
            console.log('[deleteRecording] docId or filename not found');
            return;
        }
        setValue(null);
        setBlob(elementId, null);
        setAudioUrl(null);
    };

    const openIosFile = () => {
        (window.cordova.plugins as any).fileOpener2.open(
            audioUrl,
            audioService.getSupportedMimeType(),
            {
                error: function (e: any) { console.log('Error status: ' + e.status + ' - Error message: ' + e.message); },
                success: function () { console.log('File opened successfully'); }
            }
        );
    }

    React.useEffect(() => {
        return () => {
            if (mediaRecorderRef.current?.state === 'recording') {
                try {
                    mediaRecorderRef.current.stop();
                } catch {
                    // ignore
                }
            }
            fetchIdRef.current++;
        };
    }, []);

    console.log('[InputAudio] audioUrl: ', audioUrl);

    return (
        <div className="page-input-audio">
            <label>
                <TextWithReferences text={data.label || ''} />
                <RequiredAsterisk required={data.required} />
            </label>

            <div className='page-input-audio-inner'>
                <button
                    type="button"
                    onClick={handleToggleRecording}
                    className={`page-input-audio-record-btn${isRecording ? ' recording' : ''}`}
                >
                    <svg className="mic-icon" viewBox="0 0 24 24">
                        <path d="M12 14a3 3 0 0 0 3-3V5a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 14 0h-2zm-5 9a7 7 0 0 0 7-7h-2a5 5 0 0 1-10 0H5a7 7 0 0 0 7 7z" />
                    </svg>

                    <svg className="stop-icon" viewBox="0 0 24 24">
                        <rect x="6" y="6" width="12" height="12" rx="2" ry="2" />
                    </svg>
                </button>

                <div className="page-input-audio-clips">
                    <AudioRecordingRow audioUrl={audioUrl} filename={filename} deleteRecording={deleteRecording} openIosFile={openIosFile} />
                </div>
            </div>
        </div>
    );
};
export default InputAudio;